import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { sendFormEmail } from '../../utils/formMail';
import { company } from '../../data/company';
type Status = 'idle' | 'submitting' | 'success';
export function WarrantyClaim() {
  const [form, setForm] = useState({
    email: '',
    order: '',
    purchase: 'Single Light',
    issue: 'doa',
    units: '1',
    details: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [ref, setRef] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [sendError, setSendError] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter the email used for your order.';
    if (!form.order.trim()) next.order = 'Order number is required as proof of purchase.';
    if (form.details.trim().length < 10) next.details = 'Please describe the issue (10+ characters).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    setSendError('');
    const reference = `MFL-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    sendFormEmail(`Warranty claim ${reference} — order ${form.order}`, {
      Reference: reference,
      'Order email': form.email,
      'Order number': form.order,
      Purchase: form.purchase,
      Issue: form.issue,
      'Affected lights': form.units,
      Details: form.details
    }, {
      replyTo: form.email,
      file: photo
    }).then(() => {
      track('warranty_claim_submit', {
        issue: form.issue,
        purchase: form.purchase
      });
      setRef(reference);
      setStatus('success');
    }).catch(() => {
      setStatus('idle');
      setSendError(`We couldn't submit your claim. Please try again or email ${company.supportEmail}.`);
    });
  };
  return <>
      <PageHeader title="Warranty Claim" lead="Tell us what happened. Covered defects are resolved with a replacement according to warranty terms." data-id="element-1510" data-mp="18o2238nxxtws" />
      <div className="mx-auto grid max-w-site gap-10 px-5 py-12 md:px-8 md:py-16 lg:grid-cols-[1fr_1.5fr]" data-id="element-1511" data-mp="1ih9gfj0nsk90">
        <aside className="text-titanium" data-id="element-1512" data-mp="1dopsbs4ci6py">
          <h2 className="font-display text-2xl font-semibold uppercase text-white" data-id="element-1513" data-mp="1dnoexgzux852">Before you start</h2>
          <ul className="mt-4 list-disc space-y-2 pl-5" data-id="element-1514" data-mp="l0tyqwa2ouxe">
            <li data-id="element-1515" data-mp="l0arqalynqy5">Have your order number ready.</li>
            <li data-id="element-1516" data-mp="2wofmlu76de99">A photo of the issue helps us resolve it faster.</li>
            <li data-id="element-1517" data-mp="2wm2tckvyvhgw">Accidental damage, misuse and normal wear are not covered.</li>
          </ul>
          <Link to="/warranty" className="mt-6 inline-block font-semibold text-ice hover:underline" data-id="element-1518" data-mp="31g731bwma3lo">Read warranty information</Link>
        </aside>

        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-10" data-id="element-1519" data-mp="2rokw3rrbaqoq">
          {status === 'success' ? <div role="status" className="py-10 text-center" data-id="element-1520" data-mp="3ffq97z5vb4vc">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-marine" data-id="element-1521" data-mp="3kdrpydftms3n" />
              <h2 className="mt-4 font-display text-3xl font-semibold uppercase" data-id="element-1522" data-mp="3kbnpk5h4sb84">Claim submitted</h2>
              <p className="mt-2 text-titanium" data-id="element-1523" data-mp="36g35v7tkvozn">Reference <span className="font-semibold text-white" data-id="element-1524" data-mp="3bck3csmo3fpe">{ref}</span>. We'll contact {form.email}.</p>
            </div> : <form noValidate onSubmit={submit} className="grid gap-6 md:grid-cols-2" data-id="element-1525" data-mp="1ux8vr6r99y5r">
              <FormField id="wc-email" label="Order email" error={errors.email} data-id="element-1526" data-mp="1zyogsytfo4o5">
                <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-1527" data-mp="2f7tgtowsu928" />
              </FormField>
              <FormField id="wc-order" label="Order number" error={errors.order} data-id="element-1528" data-mp="2aeqqdpnbjfq9">
                <input className={inputClasses} value={form.order} onChange={set('order')} placeholder="#1001" data-id="element-1529" data-mp="25i094yzs2e39" />
              </FormField>
              <FormField id="wc-purchase" label="What did you purchase?" data-id="element-1530" data-mp="2y65u9muqu53m">
                <select className={inputClasses} value={form.purchase} onChange={set('purchase')} data-id="element-1531" data-mp="2y4bgcshw3d91">
                  <option value="Single Light" data-id="element-1532" data-mp="2t58gdmgrnx7x">Single Light</option>
                  <option value="Duo Lighting System (2)" data-id="element-1533" data-mp="2p4es2rzewgei">Duo Lighting System (2)</option>
                  <option value="Starter Lighting System (4)" data-id="element-1534" data-mp="2k5ldew8e2jcr">Starter Lighting System (4)</option>
                  <option value="Essential Lighting System (6)" data-id="element-1535" data-mp="1drm31oy0a625">Essential Lighting System (6)</option>
                  <option value="Signature Lighting System (8)" data-id="element-1536" data-mp="18qoix6tfr1y0">Signature Lighting System (8)</option>
                  <option value="Complete Lighting System (12)" data-id="element-1537" data-mp="3kg4fmkojbmxl">Complete Lighting System (12)</option>
                  <option value="Ultimate Lighting System (24)" data-id="element-1538" data-mp="3pepjqv6wqqrb">Ultimate Lighting System (24)</option>
                </select>
              </FormField>
              <FormField id="wc-issue" label="Issue type" data-id="element-1539" data-mp="20tyw3xq0pry">
                <select className={inputClasses} value={form.issue} onChange={set('issue')} data-id="element-1540" data-mp="224zjuvvdheu">
                  <option value="doa" data-id="element-1541" data-mp="6zewdvll8vcp">Arrived defective</option>
                  <option value="power" data-id="element-1542" data-mp="by8clvs9f0gk">Won't power on / charge</option>
                  <option value="leak" data-id="element-1543" data-mp="gy4gzw4vctwn">Water ingress</option>
                  <option value="led" data-id="element-1544" data-mp="gym8ygki0lyr">LED / color fault</option>
                  <option value="mount" data-id="element-1545" data-mp="kzzh4xb2d18u">Mount base issue</option>
                  <option value="other" data-id="element-1546" data-mp="kxd9kxo6alqm">Other</option>
                </select>
              </FormField>
              <FormField id="wc-units" label="Affected lights" data-id="element-1547" data-mp="2mr2sxpwowluz">
                <input type="number" min={1} className={inputClasses} value={form.units} onChange={set('units')} data-id="element-1548" data-mp="2mrkvt5duxybv" />
              </FormField>
              <FormField id="wc-photo" label="Photo (optional)" hint="JPG or PNG" data-id="element-1549" data-mp="3l2vnmdd91nqd">
                <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] ?? null)} className="w-full text-sm text-titanium file:mr-4 file:rounded-md file:border-0 file:bg-white/10 file:px-4 file:py-2.5 file:text-white" data-id="element-1550" data-mp="3l58bt89loubv" />
              </FormField>
              <div className="md:col-span-2" data-id="element-1551" data-mp="ctvqif4tg4kt">
                <FormField id="wc-details" label="Describe the issue" error={errors.details} data-id="element-1552" data-mp="7wn422cmxedr">
                  <textarea rows={5} className={inputClasses} value={form.details} onChange={set('details')} data-id="element-1553" data-mp="ml9rysgbvegl" />
                </FormField>
              </div>
              <div className="md:col-span-2" data-id="element-1554" data-mp="hmoswwctb0tq">
                {sendError && <p role="alert" className="mb-4 text-sm text-red-300" data-id="element-1555" data-mp="vleno5c5soex">{sendError}</p>}
                <button type="submit" disabled={status === 'submitting'} className={buttonClasses('primary')} data-id="element-1556" data-mp="qn4w04pn85y2">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1557" data-mp="1a9ya1r18t3s1" />}
                  Submit claim
                </button>
              </div>
            </form>}
        </div>
      </div>
    </>;
}